import { Suspense } from 'react';
import Skeleton from './components/ui/skeleton';
import { lazyWithRetry } from './lib/lazyWithRetry';

// Lazy load all main pages for code-splitting (auto-retry on stale chunk after deploy)
const EMTDashboard = lazyWithRetry(() => import('./components/EMTDashboard'));
const EMTPerformance = lazyWithRetry(() => import('./components/EMTPerformance'));
const EMTAccountDetails = lazyWithRetry(() => import('./components/EMTAccountDetails'));
const Messages = lazyWithRetry(() => import('./components/Messages'));
const Notifications = lazyWithRetry(() => import('./components/Notifications'));
const Jobs = lazyWithRetry(() => import('./components/Jobs'));
const JobDetail = lazyWithRetry(() => import('./components/JobDetail'));
const JobApplication = lazyWithRetry(() => import('./pages/emt/JobApplication'));
const Applications = lazyWithRetry(() => import('./components/Applications'));
const FindTraining = lazyWithRetry(() => import('./pages/emt/FindTraining'));
const CareerWallet = lazyWithRetry(() => import('./pages/emt/CareerWallet'));

// Employer pages
const EmployerDashboardHome = lazyWithRetry(() => import('./pages/employer/EmployerDashboardHome'));
const EmployerDashboard = lazyWithRetry(() => import('./components/EmployerDashboard'));
const EmployerCompanySetup = lazyWithRetry(() => import('./components/EmployerCompanySetup'));
const EmployerJobForm = lazyWithRetry(() => import('./components/EmployerJobForm'));
const EmployerPostJob = lazyWithRetry(() => import('./pages/employer/EmployerPostJob'));
const EmployerPostEmergencyJob = lazyWithRetry(() => import('./pages/employer/EmployerPostEmergencyJob'));
const EmployerJobsPage = lazyWithRetry(() => import('./pages/employer/EmployerJobsPage'));
const EmployerJobApplicants = lazyWithRetry(() => import('./components/EmployerJobApplicants'));
const EmployerMessages = lazyWithRetry(() => import('./components/EmployerMessages'));
const EmployerSearch = lazyWithRetry(() => import('./components/EmployerSearch'));
const EmployerPurchasedProfiles = lazyWithRetry(() => import('./components/EmployerPurchasedProfiles'));
const EmployerAccount = lazyWithRetry(() => import('./pages/employer/EmployerAccount'));
const EmployerEditCompany = lazyWithRetry(() => import('./pages/employer/EmployerEditCompany'));
const EmployerCompanyProfile = lazyWithRetry(() => import('./pages/employer/EmployerCompanyProfile'));
const EmployerCompanyChecklist = lazyWithRetry(() => import('./pages/employer/EmployerCompanyChecklist'));
const EmployerMatches = lazyWithRetry(() => import('./pages/employer/EmployerMatches'));
const OrganizationPublicProfile = lazyWithRetry(() => import('./pages/OrganizationPublicProfile'));
const EmployerEmtProfile = lazyWithRetry(() => import('./pages/employer/EmployerEmtProfile'));
const EmployerApplications = lazyWithRetry(() => import('./pages/employer/EmployerApplications'));
const EmployerApplicationDetail = lazyWithRetry(() => import('./pages/employer/EmployerApplicationDetail'));
const EmployerProfilePurchases = lazyWithRetry(() => import('./pages/employer/EmployerProfilePurchases'));
const EmployerNotifications = lazyWithRetry(() => import('./pages/employer/EmployerNotifications'));
const EmployerWorkforce = lazyWithRetry(() => import('./pages/employer/EmployerWorkforce'));
// Job post benchmarks (pay, views, clicks vs similar employers). Was "Insights" until 2026-10-14, when the
// Results page took that name.
const EmployerJobBenchmarks = lazyWithRetry(() => import('./pages/employer/EmployerJobBenchmarks'));
const EmployerScheduling = lazyWithRetry(() => import('./pages/employer/EmployerScheduling'));
const CandidateBookingPage = lazyWithRetry(() => import('./pages/emt/BookingPage'));
const ScheduleInvitePage = lazyWithRetry(() => import('./pages/emt/ScheduleInvitePage'));
const InterviewRequestPage = lazyWithRetry(() => import('./pages/emt/InterviewRequestPage'));
const EmployerSsoBackupPassword = lazyWithRetry(() => import('./pages/employer/EmployerSsoBackupPassword'));
const EMTCompanyMap = lazyWithRetry(() => import('./pages/emt/EMTCompanyMap'));

const PastLogs = lazyWithRetry(() => import('./pages/emt/PastLogs'));
const MyCredentialing = lazyWithRetry(() => import('./pages/emt/MyCredentialing'));
const OrientationPage = lazyWithRetry(() => import('./pages/emt/OrientationPage'));
// Insights (/employer/insights): was "Results" at /employer/results (2026-10-14).
const EmployerHiringInsights = lazyWithRetry(() => import('./pages/employer/EmployerHiringInsights'));

// Public pages
const PublicProfile = lazyWithRetry(() => import('./pages/PublicProfile'));

// Loading fallback
const LoadingFallback = () => (
  <div className="p-6">
    <Skeleton className="h-10 w-64" />
    <Skeleton className="h-40 w-full mt-4" />
  </div>
);

export default function AppRoutes() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      {/* Routes will be rendered here by App.tsx */}
      {/* This component is just for lazy loading setup */}
    </Suspense>
  );
}

// Export lazy components for use in App.tsx
export {
  CareerWallet,
  EMTDashboard,
  EMTPerformance,
  EMTAccountDetails,
  Messages,
  Notifications,
  Jobs,
  JobDetail,
  JobApplication,
  Applications,
  FindTraining,
  EmployerDashboardHome,
  EmployerDashboard,
  EmployerCompanySetup,
  EmployerJobForm,
  EmployerPostJob,
  EmployerPostEmergencyJob,
  EmployerJobsPage,
  EmployerJobApplicants,
  EmployerMessages,
  EmployerSearch,
  EmployerPurchasedProfiles,
  EmployerAccount,
  EmployerEditCompany,
  EmployerCompanyProfile,
  EmployerCompanyChecklist,
  EmployerMatches,
  OrganizationPublicProfile,
  EmployerEmtProfile,
  EmployerApplications,
  EmployerApplicationDetail,
  EmployerProfilePurchases,
  EmployerNotifications,
  EmployerWorkforce,
  EmployerJobBenchmarks,
  EmployerScheduling,
  CandidateBookingPage,
  ScheduleInvitePage,
  InterviewRequestPage,
  EmployerSsoBackupPassword,
  EMTCompanyMap,
  PastLogs,
  MyCredentialing,
  OrientationPage,
  EmployerHiringInsights,
  PublicProfile,
};
